import { Box, Button } from '@ff-it/ui';
import {
  DiscountTreeEditor,
  discountContextLabels as contextLabels,
  discountPercentage as percentage,
  discountRuleLabel as ruleLabel,
  discountDiagnosticText as diagnosticText,
  indexOrderingDiagnostics,
  reorderRuleIds,
  type DiscountColumn,
  type DiscountTreeSection,
} from 'modules/agreement/discountTree';
import * as treeStyles from 'modules/agreement/discountTree/DiscountTreeEditor.css';
import type { ActivityType } from 'types';
import type { ClientAgreementVendorTerms, ClientDiscountRootOwner } from '../vendorTerms/types';
import {
  contexts,
  type ClientAgreementDiscountRule,
  type ClientDiscountTarget,
  type ClientDiscountTreeContext,
} from './types';
import { applicableDiscountContext } from 'modules/agreement/discountTree/form';
import { contextFields, validateRule } from './form';
import { orderingDiagnostics, policyOrderingDiagnostics, type ClientOrderingDiagnostic } from './orderingDiagnostics';
import { buildRootTree } from './tree';
import { FactorPolicyIndicator } from 'modules/agreement/factorPolicy/FactorPolicyIndicator';
import { policyDiagnosticText } from 'modules/agreement/factorPolicy/ordering';

export type ClientDiscountTreeProps = {
  activeType: ActivityType;
  kind: ClientDiscountRootOwner['kind'];
  roots: readonly ClientAgreementVendorTerms[];
  compact?: boolean;
  readOnly?: boolean;
  onAddRule?: (root: ClientAgreementVendorTerms) => void;
  onEditRoot?: (root: ClientAgreementVendorTerms) => void;
  onEditRule?: (root: ClientAgreementVendorTerms, rule: ClientAgreementDiscountRule) => void;
  onReorderRules?: (
    root: ClientAgreementVendorTerms,
    target: ClientDiscountTarget,
    ruleIds: readonly number[],
  ) => void | Promise<void>;
};

/** Client presentation; the calling editor owns dialogs and mutations. */
export function ClientDiscountTree({
  activeType,
  kind,
  roots,
  compact = false,
  readOnly = false,
  onAddRule,
  onEditRoot,
  onEditRule,
  onReorderRules,
}: ClientDiscountTreeProps) {
  const nodes = buildRootTree(roots);
  const visibleContexts = contexts.filter((context) =>
    applicableDiscountContext(context, activeType, null, kind === 'vendor' ? 'MEDIA' : 'SERVICE'),
  );
  const columns: DiscountColumn<ClientAgreementDiscountRule, ClientDiscountTreeContext>[] = visibleContexts.flatMap(
    (context) =>
      (['client', 'provider'] as const).map((party) => ({
        id: `${context}-${party}`,
        header: party === 'client' ? 'Client' : 'Provider',
        group: {
          id: context,
          header: context === 'standard_discount' && kind === 'service-category' ? 'Service' : contextLabels[context],
        },
        renderRule: (rule: ClientAgreementDiscountRule) => (
          <>
            {activeType === 'RADIO' && kind === 'vendor' && context === 'standard_discount' && (
              <FactorPolicyIndicator
                side={party === 'client' ? 'Client' : 'Provider'}
                policy={party === 'client' ? rule.client_factor_policy : rule.provider_factor_policy}
              />
            )}
            {percentage(rule[contextFields[context][party]])}
          </>
        ),
      })),
  );
  const diagnosticsByRule = indexOrderingDiagnostics<ClientAgreementDiscountRule, ClientOrderingDiagnostic>(
    nodes,
    orderingDiagnostics,
  );
  const policiesByRule = indexOrderingDiagnostics(nodes, policyOrderingDiagnostics);
  const sections: DiscountTreeSection<ClientAgreementDiscountRule, ClientDiscountTreeContext>[] = [
    {
      id: 'main',
      includes: (node, rule) => !Object.keys(validateRule(rule, activeType, node.owner)).length,
      includesNode: (node) => node.isRoot,
    },
    {
      id: 'attention',
      title: 'Needs attention',
      description: 'These saved rules do not match their current type, target, or section owner.',
      variant: 'attention',
      includes: (node, rule) => Object.keys(validateRule(rule, activeType, node.owner)).length > 0,
    },
  ];
  return (
    <Box minWidth="0" data-testid="client-discount-tree">
      <DiscountTreeEditor<ClientAgreementDiscountRule, ClientDiscountTreeContext>
        nodes={nodes}
        columns={columns}
        sections={sections}
        emptyText="No client discount rules yet."
        readOnly={readOnly}
        actionsWidth={compact ? '6rem' : undefined}
        presentBranch={(node) =>
          node.isRoot && compact
            ? {
                suffix: node.root.provider?.title,
                actions: (
                  <>
                    {onAddRule && (
                      <Button
                        variant="secondary"
                        size="xs"
                        icon="plus"
                        className={treeStyles.compactButton}
                        aria-label={`Add rule: ${node.owner.name}`}
                        title="Add rule"
                        onClick={() => onAddRule(node.root)}
                      />
                    )}
                    {onEditRoot &&
                      !(
                        node.rules.length === 1 &&
                        !node.rules[0]!.target_activity &&
                        !node.rules[0]!.target_category
                      ) && (
                        <Button
                          variant="secondary"
                          size="xs"
                          icon="pen"
                          className={treeStyles.compactButton}
                          aria-label={`Edit root: ${node.owner.name}`}
                          title="Edit root"
                          onClick={() => onEditRoot(node.root)}
                        />
                      )}
                  </>
                ),
              }
            : undefined
        }
        inlineRule={
          compact
            ? (node) => {
                const rule = node.rules.length === 1 ? node.rules[0] : undefined;
                return rule && !rule.target_activity && !rule.target_category ? rule : undefined;
              }
            : undefined
        }
        presentRule={(rule) => ({
          label: ruleLabel(rule),
          ariaLabel: ruleLabel(rule),
          description: rule.terms,
          diagnostic: (
            <>
              {diagnosticText(diagnosticsByRule.get(rule.id) ?? [], visibleContexts)}
              {activeType === 'RADIO' && kind === 'vendor' && policyDiagnosticText(policiesByRule.get(rule.id) ?? [])}
            </>
          ),
        })}
        onEdit={onEditRule ? (node, rule) => onEditRule(node.root, rule) : undefined}
        onReorder={
          onReorderRules
            ? (node, source, target, edge) => {
                const endpoint = source.target_path[source.target_path.length - 1]!;
                return onReorderRules(
                  node.root,
                  { kind: endpoint.kind, id: endpoint.id },
                  reorderRuleIds(node.rules, source.id, target.id, edge),
                );
              }
            : undefined
        }
      />
    </Box>
  );
}
